import { useEffect, useState } from "react";
import { Star, Quote, MessageSquarePlus, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import GoogleReviewBadge from "@/components/GoogleReviewBadge";

const initial = { name: "", email: "", rating: 5, treatment: "", message: "" };

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({ google_rating: 5.0, google_review_count: 194, google_review_url: "" });
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = "Patient Reviews — Manasvi Dental Clinic";
    api.get("/reviews").then(r => setReviews(r.data)).catch(() => {});
    api.get("/settings").then(r => setSettings(s => ({...s, ...r.data}))).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) { toast.error("Please share your name and review"); return; }
    setLoading(true);
    try {
      await api.post("/reviews", form);
      toast.success("Thank you! Your review will be published after quick moderation.");
      setSubmitted(true); setForm(initial);
    } catch {
      toast.error("Failed to submit review");
    } finally { setLoading(false); }
  };

  return (
    <div className="anim-fade-up">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-8">
        <span className="text-xs uppercase tracking-[0.25em] text-primary">Patient Stories</span>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight mt-3 max-w-3xl">Trusted by thousands.<br/>Rated by hundreds.</h1>
        <p className="mt-6 text-lg text-muted-foreground max-w-2xl">Real stories from real patients — verified by Google and our team.</p>
      </section>

      {/* GOOGLE RATING HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-6">
        <GoogleReviewBadge
          rating={settings.google_rating || 5.0}
          count={settings.google_review_count || 194}
          reviewUrl={settings.google_review_url}
          variant="default"
        />
      </section>

      {/* SITE REVIEWS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        {reviews.length === 0 ? (
          <div className="bg-muted/40 border border-border rounded-2xl p-12 text-center">
            <Quote className="w-10 h-10 text-primary/40 mx-auto mb-4" />
            <p className="text-muted-foreground max-w-md mx-auto">We are collecting website reviews too. In the meantime, check out our <strong className="text-foreground">{settings.google_review_count || 194} verified Google reviews</strong> above.</p>
          </div>
        ) : (
          <>
            <h2 className="font-serif text-3xl mb-8">Website Reviews</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((r, i) => (
                <div key={r.id} className="bg-card border border-border rounded-2xl p-8 relative" data-testid={`review-${i}`}>
                  <Quote className="w-6 h-6 text-accent absolute top-6 right-6 opacity-40" />
                  <div className="flex gap-0.5 mb-4">{[...Array(r.rating)].map((_,j)=><Star key={j} className="w-4 h-4 fill-accent text-accent" />)}</div>
                  <p className="text-foreground/90 leading-relaxed">&ldquo;{r.message}&rdquo;</p>
                  <div className="mt-6 pt-4 border-t border-border">
                    <div className="font-serif text-lg">{r.name}</div>
                    {r.treatment && <div className="text-xs text-muted-foreground uppercase tracking-widest">{r.treatment}</div>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* SHARE EXPERIENCE */}
      <section className="bg-muted/40 border-t border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-10">
          {/* GOOGLE CTA CARD */}
          <div className="bg-card border border-border rounded-2xl p-8">
            <svg viewBox="0 0 24 24" className="w-10 h-10 mb-4"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            <h3 className="font-serif text-2xl">Review us on Google</h3>
            <p className="text-muted-foreground mt-2 text-sm">Verified Google review helps other families in Kota find great dental care. It takes only 30 seconds.</p>
            <Button onClick={() => settings.google_review_url && window.open(settings.google_review_url, "_blank")}
              className="mt-6 rounded-full bg-primary hover:bg-primary/90 h-11 px-6 w-full sm:w-auto" data-testid="reviews-google-cta">
              Write a Google Review <ExternalLink className="ml-2 w-4 h-4" />
            </Button>
          </div>

          {/* SITE FORM */}
          <div>
            {submitted ? (
              <div className="bg-card border border-border rounded-2xl p-10 text-center h-full flex flex-col items-center justify-center" data-testid="review-thanks">
                <div className="w-14 h-14 rounded-full bg-primary/15 grid place-items-center mb-4"><MessageSquarePlus className="w-6 h-6 text-primary" /></div>
                <div className="font-serif text-2xl">Thank you!</div>
                <p className="text-muted-foreground mt-2">Your review is queued for moderation and will appear here shortly.</p>
                <Button onClick={() => setSubmitted(false)} className="mt-6 rounded-full" data-testid="review-add-another">Add Another</Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="bg-card border border-border rounded-2xl p-8 space-y-4" data-testid="review-form">
                <div>
                  <h3 className="font-serif text-2xl">Or leave a quick review here</h3>
                  <p className="text-xs text-muted-foreground mt-1">No Google account needed. Moderated before publishing.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Full Name*</Label>
                    <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="review-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Email (optional)</Label>
                    <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="review-email" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Rating*</Label>
                  <div className="flex gap-1" data-testid="review-rating">
                    {[1,2,3,4,5].map(n => (
                      <button type="button" key={n} onClick={() => setForm({...form, rating: n})} className="p-1" data-testid={`rating-star-${n}`}>
                        <Star className={`w-6 h-6 ${n <= form.rating ? 'fill-accent text-accent' : 'text-muted-foreground'}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Review*</Label>
                  <Textarea rows={3} value={form.message} onChange={e => setForm({...form, message: e.target.value})} required data-testid="review-message" placeholder="What made your visit special?" />
                </div>
                <Button type="submit" className="rounded-full bg-primary hover:bg-primary/90 w-full" disabled={loading} data-testid="review-submit">
                  {loading ? "Submitting..." : "Submit Review"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
